import { Injectable } from '@angular/core';
import { OrganizationRole } from 'src/app/_models/class/role';
import { Scope } from 'src/app/_models/enum/scope';
import { User } from 'src/app/_models/class/user';
import { ProductSource } from 'src/app/_models/enum/product-source';
import { CacheService } from '../cache/cache.service';
import { PortalContextService, PortalType } from '../portal-context/portal-context.service';
import { organizationCustomerAllowsPurchase } from 'src/app/_utils/organization-customer-configuration.util';

/**
 * Service for business-portal permission checks based on the active user's organization role.
 * Organization Admins can add vehicles, add products, and cancel/reactivate products.
 * Organization Members are restricted from these actions.
 */
@Injectable({
  providedIn: 'root',
})
export class BusinessPermissionService {
  constructor(
    private readonly cache: CacheService,
    private readonly portalContext: PortalContextService,
  ) {}

  /**
   * Returns the current business organization id (entityId) when in business portal, otherwise undefined.
   */
  getBusinessOrganizationId(): string | undefined {
    if (this.portalContext.getPortalContext() !== PortalType.BUSINESS) {
      return undefined;
    }
    return this.portalContext.getMyPagesRouteParams()?.entityId;
  }

  /**
   * True when the current context is the business portal.
   */
  isInBusinessPortal(): boolean {
    return this.portalContext.getPortalContext() === PortalType.BUSINESS;
  }

  /**
   * Returns true if the given user has OrganizationAdmin scope for the given organization.
   */
  isOrganizationAdminForOrg(user: User, organizationId: string): boolean {
    return user.roles.some(
      (role) =>
        role instanceof OrganizationRole &&
        role.id === organizationId &&
        (role.scopes & Scope.OrganizationAdmin) === Scope.OrganizationAdmin,
    );
  }

  /**
   * Returns true if the active user is an Organization Admin for the current business (entityId).
   * When not in business portal, returns false (no "admin for business" concept).
   */
  async isOrganizationAdminForCurrentBusiness(organizationId?: string): Promise<boolean> {
    const orgId = organizationId ?? this.getBusinessOrganizationId();
    if (!orgId) {
      return false;
    }
    const user = await this.cache.getActiveUser();
    if (!user) {
      return false;
    }
    return this.isOrganizationAdminForOrg(user, orgId);
  }

  /**
   * In business portal: true only if user is Organization Admin for the given org.
   * Outside business portal: always true (no restriction).
   */
  private async canPerformAdminOnlyAction(organizationId?: string): Promise<boolean> {
    if (!this.isInBusinessPortal()) {
      return true;
    }
    return this.isOrganizationAdminForCurrentBusiness(organizationId);
  }

  /**
   * Permission to add new vehicles in business portal.
   * Only Organization Admins; Members are restricted.
   */
  async canAddVehicles(organizationId?: string): Promise<boolean> {
    return this.canPerformAdminOnlyAction(organizationId);
  }

  /**
   * Permission to add products in business portal.
   * Organization Admins only, and only when {@link ProductSource.MyPages} is allowed on the customer org.
   */
  async canAddProducts(organizationId?: string): Promise<boolean> {
    if (!(await this.canPerformAdminOnlyAction(organizationId))) {
      return false;
    }
    if (!this.isInBusinessPortal()) {
      return true;
    }
    return this.canPurchaseViaMyPages(organizationId);
  }

  /**
   * Whether this business customer may buy through Mina sidor (capabilities.customer.purchaseChannels.myPages).
   */
  async canPurchaseViaMyPages(organizationId?: string): Promise<boolean> {
    const orgId = organizationId ?? this.getBusinessOrganizationId();
    if (!orgId) {
      return false;
    }
    try {
      const org = await this.cache.getOrganization(orgId, true);
      const customer = org.capabilities?.customer;
      if (!customer) {
        return true;
      }
      return organizationCustomerAllowsPurchase(customer, ProductSource.MyPages);
    } catch {
      return false;
    }
  }

  /**
   * Permission to cancel or reactivate products (toggle) in business portal.
   * Only Organization Admins; Members are restricted.
   */
  async canCancelOrReactivateProducts(organizationId?: string): Promise<boolean> {
    return this.canPerformAdminOnlyAction(organizationId);
  }
}
